import type { ExternalOperatorShippingDetail } from '../../services/externalOperatorShippings.endpoints';
import { getShippingGeneralColumns } from '../../config/shippingGeneralColumns';
import { getShippingStatusColumns } from '../../config/shippingStatusColumns';
import type { MarkerData } from '@/components/maps';
import { MapModal } from '@/components/maps';
import { useMemo, useState } from 'react';
import { Table } from '@/components';

interface ExternalOperatorShippingDetailsProps {
  shipping: ExternalOperatorShippingDetail;
}

const toMarker = (
  lat?: string | null,
  long?: string | null,
  label?: string
): MarkerData | null => {
  const latNum = Number(lat);
  const lngNum = Number(long);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
  if (latNum === 0 && lngNum === 0) return null;
  return { lat: latNum, lng: lngNum, label: label ?? '' };
};

const ExternalOperatorShippingDetails = ({
  shipping,
}: ExternalOperatorShippingDetailsProps) => {
  const [showDeliveryMap, setShowDeliveryMap] = useState(false);
  const [showPickupMap, setShowPickupMap] = useState(false);

  const deliveryMarker = useMemo(
    () =>
      toMarker(
        shipping.deliveryLat,
        shipping.deliveryLong,
        shipping.deliveryAddress ?? ''
      ),
    [shipping.deliveryLat, shipping.deliveryLong, shipping.deliveryAddress]
  );

  const pickupMarker = useMemo(
    () =>
      toMarker(
        shipping.originLat,
        shipping.originLong,
        shipping.originAddress ?? ''
      ),
    [shipping.originLat, shipping.originLong, shipping.originAddress]
  );

  const firstTableColumns = useMemo(
    () =>
      getShippingGeneralColumns(shipping, deliveryMarker, () =>
        setShowDeliveryMap(true)
      ),
    [shipping, deliveryMarker]
  );

  const secondTableColumns = useMemo(
    () =>
      getShippingStatusColumns(shipping, pickupMarker, () =>
        setShowPickupMap(true)
      ),
    [shipping, pickupMarker]
  );

  const rowData = [{ id: shipping.id }];

  return (
    <div className='space-y-6'>
      <Table columns={firstTableColumns} data={rowData} isLoading={false} />
      <Table columns={secondTableColumns} data={rowData} isLoading={false} />

      <MapModal
        open={showDeliveryMap}
        onClose={() => setShowDeliveryMap(false)}
        title='Dirección de entrega'
        markers={deliveryMarker ? [deliveryMarker] : []}
      />

      <MapModal
        open={showPickupMap}
        onClose={() => setShowPickupMap(false)}
        title='Dirección de origen'
        markers={pickupMarker ? [pickupMarker] : []}
      />
    </div>
  );
};

export default ExternalOperatorShippingDetails;
